import { Autocomplete, TextField, type TextFieldProps } from "@mui/material";
import { Controller, type FieldValues, type Path, useFormContext } from "react-hook-form";
import { type ElementTracking, useElementTracking } from "../../../analytics/analytics";

export const CeDropdown = <TFieldValues extends FieldValues>({
  name,
  label,
  options,
  InputProps = {},
  loading = false,
  disabled = false,
  disableClearable = false,
  analyticsCategory,
  onChange,
}: {
  name: Path<TFieldValues>;
  label: string;
  options: { label: string; value: string }[];
  loading?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  onChange?: (value: string | null) => void;
  InputProps?: TextFieldProps;
} & ElementTracking): React.JSX.Element => {
  const methods = useFormContext<TFieldValues>();
  const analyticsProps = useElementTracking(analyticsCategory, name, "dropdown");

  return (
    <Controller
      name={name}
      control={methods.control}
      render={({ field }) => (
        <Autocomplete
          disableClearable={disableClearable}
          value={field.value || null}
          onChange={(_, value) => {
            field.onChange(value);
            onChange?.(value);
          }}
          options={options.map((option) => option.value)}
          loading={loading}
          disabled={disabled}
          getOptionLabel={(option) => options.find((o) => o.value === option)?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              {...InputProps}
              label={label}
              placeholder={label}
              fullWidth
              {...analyticsProps}
            />
          )}
        />
      )}
    />
  );
};
